import { inject } from '@angular/core';
import { signalStoreFeature } from '@ngrx/signals';
import { Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events';
import { tapResponse } from '@ngrx/operators';
import { catchError, EMPTY, switchMap, tap } from 'rxjs';
import { authEvents } from './events';
import { AuthDataService } from '../../data-access/auth';
import { clearHttpCache } from '../../data-access/http-cache.interceptor';
import { ToastService } from '../../services/toast';
import { UserNavService } from '../../services/user-nav';
import { ConfigService } from '../../services/config';
import { AuthUser } from '../../models/user.model';
import { AUTH_TOKEN_KEYS, CORP_ROLES, TERRITORY_CONTEXT_KEY } from '../../constants/auth';
import { isRole } from '../../enums/role.enum';

const MOCK_SESSION_KEY = 'vantage_mock_session';

interface MockSession {
  role: string;
  name: string;
  email: string;
  territoryId: string;
}

// The e2e tenancy fixture's Gulf Coast territory (vantage-backend/tests/fixtures/tenancy_world.sql).
// The mock OIP identities are GFC staff — matching local_serve.py's fixed `/crm` dev identity
// (GFC Territory-Admin) — so a form that POSTs the caller's territory (e.g. task create, which
// requires an explicit territory_id) sends a real, fixture-matching id under the full-stack harness.
const FIXTURE_GFC_TERRITORY = '0000000f-0000-0000-0000-000000000001';

/**
 * The preset an unknown `mockLogin` role falls back to, and the `TERRITORY_ADMIN` row itself —
 * one object, so the default and the table cannot drift apart. A separate binding rather than a
 * second `MOCK_USERS[…]` lookup at the call site: the table is a `Record<string, …>`, so under
 * `noUncheckedIndexedAccess` the fallback lookup is `… | undefined` too and the `??` chain never
 * terminates in a real value.
 */
const DEFAULT_MOCK_USER: MockSession = {
  role: 'TERRITORY_ADMIN',
  name: 'Alex Admin',
  email: 'admin@vantage.dev',
  territoryId: FIXTURE_GFC_TERRITORY,
};

const MOCK_USERS: Record<string, MockSession> = {
  // Corporate portal (cross-territory — no home territory).
  SUPER_ADMIN:              { role: 'SUPER_ADMIN',              name: 'Dev SuperAdmin',   email: 'super@vantage.dev',    territoryId: '' },
  CORPORATE_WRITER:         { role: 'CORPORATE_WRITER',         name: 'Casey Corp',       email: 'corp@vantage.dev',     territoryId: '' },
  // Territory (OIP) portal — GFC staff.
  TERRITORY_ADMIN:          DEFAULT_MOCK_USER,
  TERRITORY_MANAGER:        { role: 'TERRITORY_MANAGER',        name: 'Jamie Manager',    email: 'manager@vantage.dev',  territoryId: FIXTURE_GFC_TERRITORY },
  TERRITORY_WRITER:         { role: 'TERRITORY_WRITER',         name: 'Riley Writer',     email: 'writer@vantage.dev',   territoryId: FIXTURE_GFC_TERRITORY },
  TERRITORY_READER:         { role: 'TERRITORY_READER',         name: 'Morgan Reader',    email: 'reader@vantage.dev',   territoryId: FIXTURE_GFC_TERRITORY },
  SUB_PROXY:                { role: 'SUB_PROXY',                name: 'Pat Proxy',        email: 'proxy@vantage.dev',    territoryId: FIXTURE_GFC_TERRITORY },
  // Sub portal — GFC subs.
  SUB_ADMIN:                { role: 'SUB_ADMIN',                name: 'Sage SubAdmin',    email: 'subadmin@vantage.dev', territoryId: FIXTURE_GFC_TERRITORY },
  SUB_WRITER:               { role: 'SUB_WRITER',               name: 'Sam Subcontractor', email: 'sub@vantage.dev',     territoryId: FIXTURE_GFC_TERRITORY },
  SUB_READER:               { role: 'SUB_READER',               name: 'Sid SubReader',    email: 'subreader@vantage.dev', territoryId: FIXTURE_GFC_TERRITORY },
  // Client portal — GFC clients.
  CLIENT_WRITER:            { role: 'CLIENT_WRITER',            name: 'Chris Client',     email: 'client@vantage.dev',   territoryId: FIXTURE_GFC_TERRITORY },
  CLIENT_READER:            { role: 'CLIENT_READER',            name: 'Quinn ClientReader', email: 'clientreader@vantage.dev', territoryId: FIXTURE_GFC_TERRITORY },
};

function mockUserToAuthUser(m: MockSession): AuthUser {
  // Both defaulted: `split` is typed `string[]`, so under `noUncheckedIndexedAccess` the first
  // element is `string | undefined` even though it never is.
  const [givenName = m.name, familyName = 'User'] = m.name.split(' ');
  return {
    sub: `mock-${m.role.toLowerCase()}`,
    email: m.email,
    givenName,
    familyName,
    emailVerified: 'true',
    'custom:role': m.role,
    // ⚠️ The v2 token carries NO territory claim of either spelling. `pre_token_generation` mints
    // exactly five — user_id · portal · role · entity_id · mfa_required
    // (vantage-backend .../pre_token_generation/core/identity.py; the backend test asserts
    // `custom:territory_ids` is absent, "read per-request, not baked"). Tenancy is resolved per
    // request from `user_territories` under RLS.
    //
    // This comment used to say the plural WAS minted and instruct forms to read
    // `activeTerritoryId()` for the caller's territory. Both halves were wrong, and the instruction
    // sent a task-form fix to a second dead accessor — the create still 422'd in production while
    // passing here, because the seed below makes it resolve under mock auth.
    //
    // `territoryIds` is seeded anyway, deliberately: it is what makes the dev territory switcher
    // usable. Treat it as a LOCAL FIXTURE, not a claim. Anything needing the caller's territory for
    // real must read the RLS-scoped `GET /territories` (see `account-form`, `task-form`).
    'custom:territory_id': '',
    territoryIds: m.territoryId ? [m.territoryId] : undefined,
    // A stand-in users.id for dev/mock (the real claim is minted by pre_token_generation). Non-empty
    // so the "My Tasks" fetch fires; against the real dev API it simply returns no rows (handled).
    userId: `mock-${m.role.toLowerCase()}`,
  };
}

export function withAuthEffects() {
  return signalStoreFeature(
    withEventHandlers(() => {
      const events = inject(Events);
      const authService = inject(AuthDataService);
      const dispatch = injectDispatch(authEvents);
      const toast = inject(ToastService);
      const nav = inject(UserNavService);
      const config = inject(ConfigService);

      /**
       * After tokens issue: internal staff whose token carries no MFA factor must enroll TOTP
       * first (the backend 403s them otherwise), so route them to setup; everyone else lands
       * on their portal.
       */
      const navAfterAuth = (user: AuthUser) => {
        // config.skipMfa (TEMP/dev) bypasses the forced-enrollment gate; no MFA code is removed.
        if (user.mfaRequired && !authService.tokenHasMfa() && !config.skipMfa) {
          nav.navigateToMfaSetup();
        } else {
          nav.navigateAfterLogin(user['custom:role']);
        }
      };

      /**
       * On session RESTORE (full page load — refresh/back), re-apply the territory the user had
       * switched into so the scope survives the reload (mirrors the prototype's session-stored
       * `selectedTerritoryId`). Only cross-territory roles can switch, so others are left on their
       * claim-derived context. NOT called on a fresh login — that intentionally lands on the
       * default (corporate "All Territories"), matching the prototype.
       */
      const rehydrateTerritory = (user: AuthUser) => {
        if (!(CORP_ROLES as readonly string[]).includes(user['custom:role'])) return;
        let saved: string | null = null;
        try { saved = localStorage.getItem(TERRITORY_CONTEXT_KEY); } catch { /* ignore */ }
        if (saved) dispatch.setTerritoryContext(saved);
      };

      // ─── Login ──────────────────────────────────────────────────────────────

      return {
        login$: events.on(authEvents.login).pipe(
          tap(() => dispatch.setLoading(true)),
          switchMap(({ payload: { username, password } }) =>
            authService.login(username, password).pipe(
              tapResponse({
                next: (output) => {
                  // First-sign-in forced password change (admin-provisioned users).
                  if (output.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                    dispatch.newPasswordRequired({ session: output.Session ?? '', username });
                    return;
                  }
                  // MFA challenge — redirect to 2FA page
                  if (output.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                    dispatch.mfaChallengeRequired({
                      session: output.Session ?? '',
                      username,
                    });
                    nav.navigateToVerify2fa();
                    return;
                  }
                  // Authenticator-app enrollment / SMS — not yet supported in-app.
                  if (output.ChallengeName === 'MFA_SETUP' || output.ChallengeName === 'SMS_MFA') {
                    const msg =
                      output.ChallengeName === 'MFA_SETUP'
                        ? 'This account must set up an authenticator app before signing in (coming soon) — contact your administrator.'
                        : 'SMS verification is required but not supported. Contact your administrator.';
                    toast.show({ type: 'error', message: 'Additional setup required', detail: msg });
                    dispatch.loginFailed(msg);
                    return;
                  }

                  const result = output.AuthenticationResult;
                  if (!result || !authService.saveSession(result)) {
                    dispatch.loginFailed('Authentication failed — missing tokens');
                    return;
                  }

                  // Build the session from the idToken claims (no extra round-trip).
                  const user = authService.sessionFromIdToken(result.IdToken ?? '');
                  // `isRole` subsumes the old `!user['custom:role']` emptiness check ('' is not a
                  // member) and closes the case beside it: a NON-empty claim the Role enum does not
                  // know. Signing such a user in produced an empty capability set and a wordless
                  // bounce to /auth/login from `roleLandingGuard`; failing here instead surfaces it
                  // on the sign-in form, with `sessionFromIdToken`'s console line naming the value.
                  // The on-screen copy is deliberately the EXISTING message: a new user-facing
                  // string is a rule-25 decision, not a remediation one (VG-F-051).
                  if (!user.sub || !isRole(user['custom:role'])) {
                    dispatch.loginFailed('Incomplete user profile — contact support');
                    return;
                  }

                  dispatch.getUserSuccess(user);
                  dispatch.loginSuccess();
                  dispatch.mfaStatusLoaded({ enabled: false }); // Phase 2+: check MFA settings
                  navAfterAuth(user);
                },
                error: (err: Error) => {
                  const msg = parseAuthError(err);
                  toast.show({ type: 'error', message: 'Sign in failed', detail: msg });
                  dispatch.loginFailed(msg);
                },
              }),
            ),
          ),
        ),

        // ─── Dev mock login (only active when environment.mockAuth === true) ──

        mockLogin$: events.on(authEvents.mockLogin).pipe(
          switchMap(({ payload: role }) => {
            if (!config.mockAuth) return EMPTY;
            const mockUser = MOCK_USERS[role] ?? DEFAULT_MOCK_USER;
            try { localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(mockUser)); } catch { /* ignore */ }
            const user = mockUserToAuthUser(mockUser);
            dispatch.getUserSuccess(user);
            dispatch.loginSuccess();
            dispatch.mfaStatusLoaded({ enabled: false });
            nav.navigateAfterLogin(user['custom:role']);
            return EMPTY;
          }),
        ),

        // ─── MFA challenge response ──────────────────────────────────────────

        respondToMfaChallenge$: events.on(authEvents.respondToMfaChallenge).pipe(
          tap(() => dispatch.setLoading(true)),
          switchMap(({ payload: { code } }) =>
            authService.respondToMfaChallenge(code).pipe(
              tapResponse({
                next: (output) => {
                  const result = output.AuthenticationResult;
                  if (!result || !authService.saveSession(result)) {
                    dispatch.loginFailed('MFA verification failed');
                    return;
                  }

                  const user = authService.sessionFromIdToken(result.IdToken ?? '');
                  dispatch.getUserSuccess(user);
                  dispatch.loginSuccess();
                  // Completing the TOTP challenge means MFA is satisfied this session — go
                  // straight to the portal (never re-route to setup).
                  nav.navigateAfterLogin(user['custom:role']);
                },
                error: (err: Error) => {
                  const msg = parseAuthError(err);
                  toast.show({ type: 'error', message: 'MFA verification failed', detail: msg });
                  dispatch.loginFailed(msg);
                },
              }),
            ),
          ),
        ),

        // ─── New-password challenge (force-change on first sign-in) ──────────

        completeNewPassword$: events.on(authEvents.completeNewPassword).pipe(
          tap(() => dispatch.setLoading(true)),
          switchMap(({ payload: { username, newPassword } }) =>
            authService.respondToNewPasswordChallenge(newPassword).pipe(
              tapResponse({
                next: (output) => {
                  // Cognito may chain into MFA after the password is set.
                  if (output.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                    dispatch.mfaChallengeRequired({ session: output.Session ?? '', username });
                    nav.navigateToVerify2fa();
                    return;
                  }
                  if (output.ChallengeName === 'MFA_SETUP') {
                    const msg = 'Password set. This account must now set up an authenticator app (coming soon) — contact your administrator.';
                    toast.show({ type: 'error', message: 'Additional setup required', detail: msg });
                    dispatch.loginFailed(msg);
                    return;
                  }
                  const result = output.AuthenticationResult;
                  if (!result || !authService.saveSession(result)) {
                    dispatch.loginFailed('Could not set the new password. Please try again.');
                    return;
                  }
                  const user = authService.sessionFromIdToken(result.IdToken ?? '');
                  dispatch.getUserSuccess(user);
                  dispatch.loginSuccess();
                  dispatch.mfaStatusLoaded({ enabled: false });
                  navAfterAuth(user);
                },
                error: (err: Error) => {
                  const msg = parseAuthError(err);
                  toast.show({ type: 'error', message: 'Could not set password', detail: msg });
                  dispatch.loginFailed(msg);
                },
              }),
            ),
          ),
        ),

        // ─── TOTP MFA enrollment (internal staff; Cognito challenge flow) ────

        startMfaSetup$: events.on(authEvents.startMfaSetup).pipe(
          switchMap(() =>
            authService.startMfaSetup().pipe(
              tapResponse({
                next: (secret) => dispatch.mfaSecretLoaded(secret),
                error: (err: Error) => {
                  const msg = parseAuthError(err);
                  toast.show({ type: 'error', message: 'Could not start authenticator setup', detail: msg });
                  dispatch.loginFailed(msg);
                },
              }),
            ),
          ),
        ),

        submitMfaSetup$: events.on(authEvents.submitMfaSetup).pipe(
          switchMap(({ payload: code }) =>
            authService.verifyMfaSetup(code).pipe(
              tapResponse({
                next: () => {
                  toast.show({
                    type: 'success',
                    message: 'Authenticator added',
                    detail: 'Sign in again to finish enabling two-factor authentication.',
                  });
                  // Re-login so Cognito issues a token that carries the MFA factor.
                  dispatch.logout();
                  nav.navigateToLogin();
                },
                error: (err: Error) => {
                  const msg = parseAuthError(err);
                  toast.show({ type: 'error', message: 'Could not verify code', detail: msg });
                  dispatch.loginFailed(msg);
                },
              }),
            ),
          ),
        ),

        // ─── Logout ─────────────────────────────────────────────────────────

        // ─── Territory switcher: persist the selection across refreshes ──────
        // The interceptor reads AuthStore.activeTerritoryId at request time; persisting it here
        // lets restoreSession (above) re-apply it after a full page load. null ("All Territories")
        // clears the key.
        persistTerritoryContext$: events.on(authEvents.setTerritoryContext).pipe(
          tap(({ payload: territoryId }) => {
            try {
              if (territoryId) localStorage.setItem(TERRITORY_CONTEXT_KEY, territoryId);
              else localStorage.removeItem(TERRITORY_CONTEXT_KEY);
            } catch { /* ignore */ }
            // A territory-scoped TTL cache entry minted under the old scope must not survive
            // the switch — httpCacheInterceptor keys on X-Territory-Id, but a stale entry under
            // the previous scope would otherwise sit in memory unread rather than being cleared.
            clearHttpCache();
          }),
          switchMap(() => EMPTY),
        ),

        logout$: events.on(authEvents.logout).pipe(
          tap(() => {
            authService.clearSession();
            try {
              localStorage.removeItem(MOCK_SESSION_KEY);
              localStorage.removeItem(TERRITORY_CONTEXT_KEY);
            } catch { /* ignore */ }
            clearHttpCache();
            dispatch.logoutSuccess();
            // Auth guards only fire on navigation, so an in-place sign-out leaves the user on
            // the current page — navigate to login explicitly.
            nav.navigateToLogin();
          }),
          switchMap(() => EMPTY),
        ),

        // ─── Restore session on app init ─────────────────────────────────────

        restoreSession$: events.on(authEvents.restoreSession).pipe(
          switchMap(() => {
            // Dev mock: restore from localStorage mock session
            if (config.mockAuth) {
              try {
                const raw = localStorage.getItem(MOCK_SESSION_KEY);
                if (raw) {
                  const mockUser: MockSession = JSON.parse(raw);
                  const user = mockUserToAuthUser(mockUser);
                  dispatch.restoreSessionSuccess(user);
                  rehydrateTerritory(user);
                  return EMPTY;
                }
              } catch { /* corrupt — fall through to failed */ }
              dispatch.restoreSessionFailed();
              return EMPTY;
            }

            const idToken = localStorage.getItem(AUTH_TOKEN_KEYS.idToken);
            const accessToken = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken);

            if (!idToken || !accessToken || !authService.isTokenValid(idToken)) {
              // Try to refresh if we have a refresh token
              const refreshToken = localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken);
              if (!refreshToken) {
                dispatch.restoreSessionFailed();
                return EMPTY;
              }

              return authService.refreshSession().pipe(
                tapResponse({
                  next: (output) => {
                    const result = output.AuthenticationResult;
                    if (!result || !authService.saveSession(result)) {
                      dispatch.restoreSessionFailed();
                      return;
                    }
                    const user = authService.sessionFromIdToken(result.IdToken ?? '');
                    if (!user.sub) {
                      dispatch.restoreSessionFailed();
                      return;
                    }
                    dispatch.restoreSessionSuccess(user);
                    rehydrateTerritory(user);
                    if (user.mfaRequired && !authService.tokenHasMfa() && !config.skipMfa) nav.navigateToMfaSetup();
                  },
                  error: () => dispatch.restoreSessionFailed(),
                }),
              );
            }

            // Token still valid — rebuild the session from the stored idToken claims.
            const user = authService.sessionFromIdToken(idToken);
            if (!user.sub) {
              dispatch.restoreSessionFailed();
              return EMPTY;
            }
            dispatch.restoreSessionSuccess(user);
            rehydrateTerritory(user);
            if (user.mfaRequired && !authService.tokenHasMfa() && !config.skipMfa) nav.navigateToMfaSetup();
            return EMPTY;
          }),
          // A synchronous throw in the real-mode branch above — e.g. localStorage blocked in a
          // private/partitioned context, or a Cognito-client init failure on bad config — would
          // otherwise error this stream WITHOUT dispatching a settle event, hanging the bootstrap
          // initializer (app.config.ts) on its full 8s timeout. Always settle as failed instead so
          // exactly one of restoreSessionSuccess/Failed fires on every path.
          catchError(() => {
            dispatch.restoreSessionFailed();
            return EMPTY;
          }),
        ),

        // ─── Forgot / Reset password ─────────────────────────────────────────

        forgotPassword$: events.on(authEvents.forgotPassword).pipe(
          tap(() => dispatch.setLoading(true)),
          switchMap(({ payload: email }) =>
            authService.forgotPassword(email).pipe(
              tapResponse({
                next: () => {
                  dispatch.setLoading(false);
                  toast.show({
                    type: 'success',
                    message: 'Check your email',
                    detail: `A reset code was sent to ${email}`,
                  });
                },
                error: (err: Error) => {
                  toast.show({ type: 'error', message: 'Request failed', detail: err.message });
                  dispatch.loginFailed(err.message);
                },
              }),
            ),
          ),
        ),

        confirmPasswordReset$: events.on(authEvents.confirmPasswordReset).pipe(
          tap(() => dispatch.setLoading(true)),
          switchMap(({ payload: { email, code, newPassword } }) =>
            authService.confirmForgotPassword(email, code, newPassword).pipe(
              tapResponse({
                next: () => {
                  dispatch.setLoading(false);
                  // The reset page renders its confirmation screen off THIS event. Before it
                  // existed the page set a local `success` flag straight after dispatching, so a
                  // rejected reset still rendered "Your password has been reset successfully" and
                  // the error banner it had just switched away from was unreachable.
                  dispatch.passwordResetSucceeded();
                  toast.show({
                    type: 'success',
                    message: 'Password reset',
                    detail: 'You can now sign in with your new password.',
                  });
                },
                error: (err: Error) => {
                  toast.show({ type: 'error', message: 'Reset failed', detail: err.message });
                  dispatch.loginFailed(err.message);
                },
              }),
            ),
          ),
        ),

        // Self-service change-password (signed-in) — Cognito SDK; the component drives its own UI
        // off the success/failed events (no REST /auth/*).
        changePassword$: events.on(authEvents.changePassword).pipe(
          switchMap(({ payload: { currentPassword, newPassword } }) =>
            authService.changePassword(currentPassword, newPassword).pipe(
              tapResponse({
                next: () => dispatch.changePasswordSuccess(),
                error: (err: Error) => dispatch.changePasswordFailed(err.message),
              }),
            ),
          ),
        ),
      };
    }),
  );
}

/** Converts raw Cognito SDK errors to a user-friendly message string. */
function parseAuthError(err: unknown): string {
  if (err instanceof Error) {
    const name = (err as { name?: string }).name ?? '';
    switch (name) {
      case 'NotAuthorizedException':       return 'Incorrect email or password.';
      case 'UserNotFoundException':        return 'No account found with that email.';
      case 'UserNotConfirmedException':    return 'Account not verified — check your email.';
      case 'PasswordResetRequiredException': return 'Password reset required.';
      case 'TooManyRequestsException':     return 'Too many attempts — please wait and try again.';
      case 'CodeMismatchException':        return 'Invalid verification code.';
      case 'ExpiredCodeException':         return 'Verification code expired — request a new one.';
      case 'UserLambdaValidationException': {
        // Cognito wraps trigger errors, e.g. the pre_auth block:
        // "PreAuthentication failed with error Account is inactive. Contact your administrator."
        // `(\S.*)` not `(.+)`: requiring a non-space first char removes the `\s+`/`.+` overlap
        // that made the old pattern backtrack quadratically on non-matching input (SonarQube).
        const m = /failed with error\s+(\S.*)$/i.exec(err.message);
        // `m?.[1] ?? …`, not `m ? m[1] : …`: a matched regex's capture group is still typed
        // `string | undefined`, and the fallback is the same message either way.
        return (m?.[1] ?? err.message).trim();
      }
      default:                             return err.message;
    }
  }
  return 'An unexpected error occurred.';
}
